import { ExternalLink } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Typography, typographyVariants } from '@/components/ui/typography';
import { cn } from '@/lib/utils';
import { buildRoute } from '@/config/routes';
import { useNavigationService } from '@/services/navigate-service';
import { VENDOR_TAB_KEYS } from '../pms-configuration/components/VendorTabNav';
import { vendorRegistry } from '../vendor-settings/vendorRegistry';
import type { VendorDefinition } from '../vendor-settings/types';
import { comingSoonVendors } from './comingSoon';
import { landingCopy as copy } from './copy';
import { ComingSoonTile, DIRECTORY_GRID, LANDING_CARD, SETUP_GRID, VendorLogo } from '../components/VendorDirectory';
import { ComingSoonInterestLink } from './ComingSoonInterestLink';

const SetUpCard = ({ vendor }: { vendor: VendorDefinition }) => {
  const { navigateTo } = useNavigationService();
  const route = buildRoute.integrationVendor(vendor.key);
  return (
    <Card className={`${LANDING_CARD} flex flex-col gap-3.5 p-5`}>
      <div className="flex h-7 items-center">
        <VendorLogo name={vendor.displayName} logo={vendor.logo} logoHeightPx={vendor.logoHeightPx} />
      </div>
      <Typography as="p" variant="body-sm" className="text-muted-foreground">{vendor.directoryDescription}</Typography>
      <Typography as="p" variant="body-sm" className="text-muted-foreground">{vendor.connectNote}</Typography>
      <div className="mt-auto flex flex-wrap items-center gap-3">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => navigateTo(route)}
          aria-label={`${copy.firstRun.setUp} ${vendor.displayName}`}
        >
          {copy.firstRun.setUp}
        </Button>
        <Button
          type="button"
          variant="link"
          className={cn(typographyVariants({ variant: 'body-sm-medium' }), 'text-primary max-lg:min-h-11')}
          aria-label={`${copy.firstRun.learnMore} about ${vendor.displayName}`}
          onClick={() => navigateTo(route, { Tab: VENDOR_TAB_KEYS.OVERVIEW })}
        >
          {copy.firstRun.learnMore} <ExternalLink className="ml-1 h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      </div>
    </Card>
  );
};

/** No PMS connected yet (status `none`): pick a vendor (spec §4.3). */
export const FirstRunLanding = () => (
  <div className="flex flex-col gap-7">
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <Typography as="h2" variant="heading-md" className="text-lg leading-7">
          {copy.firstRun.headline}
        </Typography>
        <Typography as="p" variant="body" className="text-muted-foreground">{copy.firstRun.subline}</Typography>
      </div>
      <div className={SETUP_GRID}>
        {Object.values(vendorRegistry).map((vendor) => <SetUpCard key={vendor.key} vendor={vendor} />)}
      </div>
    </div>
    <div className={DIRECTORY_GRID}>
      {comingSoonVendors.map((vendor) => (
        <ComingSoonTile key={vendor.name} vendor={vendor} label={copy.directory.comingSoon} footer={<ComingSoonInterestLink />} />
      ))}
    </div>
  </div>
);
